/* LDW: Shopify email signup -> editable coupon screen. */
(() => {
  'use strict';
  let cleanup = () => {};
  const editorPreview = { setting: null, choice: 'email' };
  const memory = new Map(), ttl = 30 * 60 * 1000;
  const fresh = v => v && Number.isFinite(v.time) && Date.now() - v.time >= 0 && Date.now() - v.time < ttl;
  const object = raw => { const v = JSON.parse(raw); return v && typeof v === 'object' && !Array.isArray(v) ? v : null; };
  function read(key) {
    try { const v = object(localStorage.getItem(key)); if (v) return v; } catch {}
    try { const raw = document.cookie.split('; ').find(x => x.startsWith(`${key}=`)); if (raw) { const v = object(decodeURIComponent(raw.slice(key.length + 1))); if (v) return v; } } catch {}
    return memory.get(key) || {};
  }
  function write(key, value) {
    memory.set(key, value);
    try { localStorage.setItem(key, JSON.stringify(value)); return; } catch {}
    try { document.cookie = `${key}=${encodeURIComponent(JSON.stringify(value))}; Path=/; Max-Age=34560000; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`; } catch {}
  }
  function sessionRead(key) {
    try { const v = object(sessionStorage.getItem(key)); if (v) return v; } catch {}
    return key.endsWith('-pending') ? read(key) : null;
  }
  function sessionWrite(key, value) {
    try { if (value === null) sessionStorage.removeItem(key); else sessionStorage.setItem(key, JSON.stringify(value)); } catch {}
    // Never put the email itself in persistent storage or the URL.
    if (key.endsWith('-pending')) write(key, value || {});
  }
  function initialize() {
    cleanup();
    cleanup = () => {};
    const host = document.querySelector('[data-lead-popup]');
    if (!host) return;
    const dialog = host.querySelector('#LDWSignup'), form = host.querySelector('#LDWEmailForm');
    // Shopify must finish its own verification before this signup has a result.
    // Leave its form and the pending attempt untouched throughout the challenge.
    const challengePage = /^\/(?:[a-z]{2,3}(?:-[a-z0-9]{2,8})?\/)?challenge\/?$/i.test(location.pathname)
      || Boolean(document.querySelector('.shopify-challenge__container'));
    host.hidden = challengePage;
    if (challengePage) {
      if (dialog?.open) dialog.close();
      return;
    }
    if (!dialog || !form) return;
    const button = host.querySelector('[data-popup-open]');
    const editor = Boolean(window.Shopify?.designMode) || host.dataset.designMode === 'true';
    const key = `ldw-offer-${(host.dataset.campaign || 'overdrive').replace(/[^a-zA-Z0-9_-]/g, '')}`;
    const pendingKey = `${key}-pending`, emailKey = `${key}-email`;
    const limit = Math.max(3, Number(host.dataset.dismissLimit) || 3);
    const controller = new AbortController();
    const listen = (el, type, fn, capture = false) => el?.addEventListener(type, fn, { signal: controller.signal, capture });
    let timer, submitTimer, previousFocus, stage = 'email';
    const showStage = name => {
      stage = name === 'success' ? 'success' : 'email';
      host.querySelectorAll('[data-signup-stage]').forEach(el => el.hidden = el.dataset.signupStage !== stage);
      dialog.setAttribute('aria-labelledby', stage === 'success' ? 'LDWCouponTitle' : 'LeadTitle');
      host.querySelectorAll('[data-editor-step]').forEach(el => el.setAttribute('aria-pressed', String(el.dataset.editorStep === stage)));
    };
    const status = message => {
      const el = host.querySelector('[data-signup-status]');
      if (el) { el.textContent = message; el.hidden = !message; }
    };
    const open = () => {
      if (dialog.open) return;
      previousFocus = document.activeElement;
      if (editor) dialog.setAttribute('open', '');
      else { dialog.showModal(); host.querySelector('[data-popup-close]')?.focus(); }
    };
    const close = () => {
      if (!dialog.open) return;
      if (editor) dialog.removeAttribute('open'); else dialog.close();
      if (!editor && stage !== 'success' && !read(key).submitted) {
        const state = read(key);
        write(key, { ...state, dismissals: Math.min(limit, (Number(state.dismissals) || 0) + 1) });
      }
      if (!editor) previousFocus?.focus?.();
    };
    const complete = () => {
      write(key, { dismissals: Number(read(key).dismissals) || 0, submitted: true });
      sessionWrite(pendingKey, null); sessionWrite(emailKey, null);
      if (button) button.hidden = true;
    };
    cleanup = () => {
      clearTimeout(timer); clearTimeout(submitTimer); controller.abort();
      if (dialog.open) { if (editor) dialog.removeAttribute('open'); else dialog.close(); }
    };
    listen(dialog, 'cancel', e => { e.preventDefault(); close(); });
    listen(dialog, 'click', e => {
      if (!editor && e.target === dialog) {
        const r = dialog.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) close();
      }
    });
    host.querySelectorAll('[data-popup-close]').forEach(el => listen(el, 'click', close));
    listen(button, 'click', () => { showStage(read(key).submitted ? 'success' : 'email'); open(); });

    // Preview is independent of live dismissal history and never posts a signup.
    if (editor) {
      host.dataset.designMode = 'true'; dialog.classList.add('lead-popup--editor');
      if (button) button.hidden = true;
      const configured = host.dataset.editorStage === 'success' ? 'success' : 'email';
      if (editorPreview.setting !== configured) { editorPreview.setting = configured; editorPreview.choice = configured; }
      const preview = choice => { editorPreview.choice = choice; showStage(choice); open(); };
      preview(editorPreview.choice);
      listen(host, 'submit', e => { if (e.target === form) { e.preventDefault(); e.stopImmediatePropagation(); preview('success'); } }, true);
      host.querySelectorAll('[data-editor-step]').forEach(el => listen(el, 'click', () => preview(el.dataset.editorStep)));
      listen(document, 'shopify:section:select', e => { if (e.target.contains(host)) open(); });
      return;
    }
    // Liquid can display the result without JavaScript; upgrade its open dialog to a modal.
    if (dialog.open) dialog.close();
    const result = host.querySelector('[data-email-result]');
    const errors = result?.dataset.errors === 'true';
    const accepted = result?.dataset.success === 'true' && !errors;
    let cached = sessionRead(emailKey);
    if (!fresh(cached)) { cached = null; sessionWrite(emailKey, null); }
    const email = form.querySelector('[name="contact[email]"]');
    if (email && !email.value && cached?.email) email.value = cached.email;
    const pending = sessionRead(pendingKey);
    const returnedAttempt = fresh(pending) && pending.type === 'email';
    let force = false;
    // Only Shopify's server-rendered customer-form result completes the signup.
    // A submit click, contact-form receipt, URL parameter or app event cannot unlock it.
    if (accepted) {
      showStage('success'); complete(); force = true;
      const url = new URL(location.href);
      url.searchParams.delete('customer_posted');
      if (['#LDWEmailForm', '#contact_form'].includes(url.hash)) url.hash = '';
      history.replaceState(history.state, '', url);
    } else {
      showStage('email');
      if (errors || returnedAttempt) {
        force = true;
        if (!errors) status('Your signup hasn’t been confirmed yet. Please try again and complete any verification shown.');
      }
      if (pending?.time) sessionWrite(pendingKey, null);
    }
    if (host.dataset.enabled !== 'true' && !accepted) { if (button) button.hidden = true; return; }
    // Native POST is deliberately preserved, including Shopify's CAPTCHA handling.
    // Capture the attempt before CAPTCHA handlers on the form defer the submission.
    listen(host, 'submit', e => {
      if (e.target !== form) return;
      if (!form.checkValidity()) { e.preventDefault(); form.reportValidity(); return; }
      sessionWrite(emailKey, { email: email.value.trim(), time: Date.now() });
      sessionWrite(pendingKey, { type: 'email', time: Date.now() });
      status('Submitting…'); clearTimeout(submitTimer);
      submitTimer = setTimeout(() => status('Still waiting for confirmation. Complete any verification shown, or try submitting again.'), 20000);
    }, true);
    listen(document, 'submit', e => { if (e.target !== form) sessionWrite(pendingKey, null); }, true);
    if (read(key).submitted) { if (button) button.hidden = true; if (!accepted) force = false; }
    const automatic = () => {
      if (!host.isConnected) return;
      const state = read(key);
      if (state.submitted || (Number(state.dismissals) || 0) >= limit) return;
      if (document.querySelector('dialog[open]') || document.hidden) { timer = setTimeout(automatic, 1000); return; }
      open();
    };
    if (force) open(); else timer = setTimeout(automatic, Math.min(4, Math.max(1, Number(host.dataset.delay) || 3)) * 1000);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize); else initialize();
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', e => { if (e.target.querySelector('[data-lead-popup]')) cleanup(); });
  window.addEventListener('pageshow', e => { if (e.persisted) initialize(); });
})();
